import { Button, Card } from '@skupreme/shared/ui';
import { FormCheckbox } from '@skupreme/shared/ui/form';
import { StorageProviderHelpText } from '@utility/constants/strings';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { KindLabel } from '../KindLabel';
import { PermissionGate } from '../PermissionGate';

type KindValues = { is_fulfillable: boolean; is_warehouse: boolean };

/**
 * A provider that is neither a fulfillment center nor a warehouse has no
 * inventory to show; this asks which it is, in place of the table.
 */
export function ProviderKindCard({
  providerId,
  isUser,
  onSaved,
}: {
  providerId: string;
  isUser: boolean;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const { control, handleSubmit, watch } = useForm<KindValues>({
    defaultValues: { is_fulfillable: false, is_warehouse: false },
  });
  const isPicked = watch('is_fulfillable') || watch('is_warehouse');

  const save = handleSubmit(async values => {
    try {
      setSaving(true);
      await StorageProviderService.updateType(providerId, values);
      ToastMessage.success('Storage provider updated successfully.');
      onSaved();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card
      title="Configure Storage Provider"
      className="sk:mx-auto sk:max-w-160"
    >
      <form noValidate onSubmit={save} className="sk:flex sk:flex-col sk:gap-4">
        <p className="sk:m-0 sk:text-dense sk:text-fg-secondary">
          Your storage provider is not configured properly. It has to be at
          least one of the following options:
        </p>
        <div className="sk:grid sk:grid-cols-2 sk:gap-4">
          <FormCheckbox
            control={control}
            name="is_fulfillable"
            hint={StorageProviderHelpText.typeTooltip}
            label={
              <KindLabel
                title="Fulfills Individual Orders"
                detail="(Stores and Ships Individual Items)"
              />
            }
            isDisabled={isUser}
          />
          <FormCheckbox
            control={control}
            name="is_warehouse"
            hint={StorageProviderHelpText.warehouseTypeTooltip}
            label={
              <KindLabel
                title="Offers Storage and Forwarding"
                detail="(Stores Parcels / Pallets)"
              />
            }
            isDisabled={isUser}
          />
        </div>
        <div className="sk:flex sk:justify-end">
          <PermissionGate disabled={isUser} hidden={false}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!isPicked || isUser}
              isPending={saving}
            >
              Save
            </Button>
          </PermissionGate>
        </div>
      </form>
    </Card>
  );
}
